import { motion } from "framer-motion";
import { Phone, ArrowRight, MapPin, CheckCircle2, Percent } from "lucide-react";
import { SITE, buildWhatsAppLink } from "../../lib/site";
import { Stamp } from "../Stamp";

const STUDENTS = ["/students/elev-3.jpg", "/students/elev-2.jpg", "/students/elev-1.jpg"];

const STATS = [
  { value: "500+", label: "Elevi promovați" },
  { value: "100%", label: "Instructori autorizați" },
  { value: "20 ani", label: "Experiență" },
  { value: "6", label: "Mașini moderne" },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.09 } } };
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 lg:pt-44">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Copy — șase coloane, compoziție editorială */}
        <motion.div variants={container} initial="hidden" animate="show" className="lg:col-span-6">
          <motion.div variants={item} className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/60 px-4 py-1.5">
            <MapPin size={14} className="text-[#1565C0]" />
            <span className="text-xs font-semibold tracking-wide text-[#1565C0] uppercase">Pașcani · Categoria B</span>
          </motion.div>

          <motion.h1 variants={item} className="mt-6 font-display font-extrabold text-slate-900 text-[2.7rem] leading-[1.05] sm:text-6xl lg:text-[4.2rem] tracking-tight">
            Sute de elevi au luat <span className="text-[#1565C0]">permisul</span> cu noi.
            <span className="block text-slate-400 font-bold">Urmezi tu.</span>
          </motion.h1>

          <motion.p variants={item} className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
            Fiecare elev are propriul ritm. Noi suntem aici să te pregătim pas cu pas, până în ziua în care iei permisul.
          </motion.p>

          <motion.div variants={item} className="mt-9 flex flex-col sm:flex-row gap-3.5">
            <a
              href={buildWhatsAppLink("Bună ziua! Doresc să mă înscriu la școala auto Auto Valy Drive pentru categoria B.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-enroll-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#1565C0] text-white hover:bg-[#0D47A1] rounded-full px-8 py-4 font-semibold transition-all brand-shadow hover:-translate-y-0.5"
            >
              Înscrie-te acum <ArrowRight size={18} />
            </a>
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1565C0] border-2 border-[#1565C0] hover:bg-blue-50 rounded-full px-8 py-4 font-semibold transition-all"
            >
              <Phone size={18} /> Sună acum
            </a>
          </motion.div>

          <motion.div variants={item} className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-4 py-2">
            <Percent size={14} className="text-[#1565C0]" />
            <span className="text-sm font-semibold text-[#0D47A1]">20% reducere pentru elevi, studenți, pensionari și șomeri</span>
          </motion.div>
        </motion.div>

        {/* Fotografia ca obiect editorial — print montat, ștampilat, coborât peste linia de credit */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative z-10 lg:-mb-16"
        >
          <div className="relative bg-white border border-slate-200 rounded-[36px] p-3">
            <div className="relative h-[400px] sm:h-[500px] lg:h-[540px] rounded-[28px] overflow-hidden">
              <img
                src="/students/elev-3.jpg"
                alt="Eleve promovate la Auto Valy Drive"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "50% 20%" }}
              />
              <div className="absolute inset-0 grain-overlay opacity-[0.08] pointer-events-none" />
            </div>
          </div>

          {/* ștampila — pe rama printului, ca un cachet */}
          <Stamp ink="blue" className="absolute -top-4 right-8" />

          {/* 1:1 — foaie discretă, suprapusă pe margine */}
          <div className="absolute -left-3 sm:-left-5 top-16 bg-white rounded-2xl border border-slate-200 px-5 py-4">
            <div className="font-display font-extrabold text-2xl text-[#1565C0]">1:1</div>
            <div className="text-xs text-slate-500 font-medium">instruire</div>
          </div>

          {/* dovada — foaie albă sub print */}
          <div className="absolute -bottom-5 left-8 flex items-center gap-2 bg-white rounded-2xl border border-slate-200 px-4 py-3">
            <CheckCircle2 className="text-[#1565C0] shrink-0" size={22} />
            <span className="text-sm font-semibold text-slate-900">Permis obținut · Examen luat</span>
          </div>
        </motion.div>
      </div>

      {/* Linia de credit — cifrele ca semnătură sub fotografia copertă */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
        data-testid="hero-stat-strip"
        className="mt-16 lg:mt-24 border-t border-slate-200 pt-8 pb-12 lg:pb-16 flex flex-wrap items-center gap-x-10 gap-y-5"
      >
        {STATS.map((s) => (
          <div key={s.label} data-testid={`hero-stat-${s.label}`} className="flex items-baseline gap-2.5">
            <span className="font-display font-extrabold text-2xl lg:text-3xl text-slate-900 tracking-tight">{s.value}</span>
            <span className="text-sm text-slate-500">{s.label}</span>
          </div>
        ))}
        <div className="lg:ml-auto flex items-center gap-3">
          <div className="flex -space-x-2.5">
            {STUDENTS.map((s, i) => (
              <img key={i} src={s} alt="Elev promovat" className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm" style={{ objectPosition: "50% 30%" }} />
            ))}
          </div>
          <div>
            <div className="font-display font-bold text-sm text-slate-900 leading-tight">Sute de elevi promovați</div>
            <div className="text-xs text-slate-500">Te pregătim până iei permisul</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default Hero;
